/**
 * ProseMirror plugin to highlight referenced text in notes
 * When a reference is active in the Anni chat, this plugin applies decorations
 * to highlight the referenced text range in the note editor
 */
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view';
import { Note, UUID } from '@annote/types';

export interface HighlightedReference {
  noteId: UUID;
  textStart: number;
  textEnd: number;
}

interface HighlightReferencesPluginOptions {
  note: Note;
  getHighlightedReferences: () => HighlightedReference[];
}

const highlightReferencesPluginKey = new PluginKey('highlightReferences');

export const highlightReferencesPlugin = (options: HighlightReferencesPluginOptions): Plugin =>
  new Plugin({
    key: highlightReferencesPluginKey,
    state: {
      init(): DecorationSet {
        return DecorationSet.empty;
      },
      apply(tr, oldDecorations): DecorationSet {
        // If document changed, we need to map decorations
        if (tr.docChanged) {
          return oldDecorations.map(tr.mapping, tr.doc);
        }

        // Check if we need to update decorations based on highlighted references
        const meta = tr.getMeta(highlightReferencesPluginKey);
        if (meta && meta.updateDecorations) {
          const refs = options.getHighlightedReferences();
          const relevantRefs = refs.filter((ref) => ref.noteId === options.note.id);

          if (relevantRefs.length === 0) {
            return DecorationSet.empty;
          }

          const decorations = relevantRefs.map((ref) => {
            // Convert text positions to ProseMirror positions
            // textStart/textEnd are character positions in the note's text
            // ProseMirror positions include node boundaries, so we add 1 for the doc node
            const docOffset = 1;
            const proseMirrorFrom = ref.textStart + docOffset;
            const proseMirrorTo = ref.textEnd + docOffset;

            // Ensure positions are within document bounds
            const docSize = tr.doc.content.size;
            const from = Math.max(0, Math.min(proseMirrorFrom, docSize));
            const to = Math.max(from, Math.min(proseMirrorTo, docSize));

            return Decoration.inline(from, to, {
              class: 'highlighted-reference',
              style: 'background-color: rgba(209, 213, 219, 0.5); border-radius: 2px;',
            });
          });

          return DecorationSet.create(tr.doc, decorations);
        }

        return oldDecorations;
      },
    },
    props: {
      decorations(state) {
        return this.getState(state);
      },
    },
  });

/**
 * Helper function to trigger decoration updates
 * Call this when the highlighted references change
 */
export const updateHighlightDecorations = (view: EditorView): void => {
  if (!view) return;
  const { state } = view;
  const { tr } = state;
  tr.setMeta(highlightReferencesPluginKey, { updateDecorations: true });
  view.dispatch(tr);
};
